import React, { useEffect, useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Button,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Tooltip,
  List,
  ListItem,
  ListItemText,
  Divider,
  Collapse,
  Stack,
  TextField,
  FormControl,
  FormControlLabel,
  Checkbox,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Person as PersonIcon,
  School as SchoolIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  AccessTime as AccessTimeIcon,
  AttachMoney as AttachMoneyIcon,
  Group as GroupIcon,
  Event as EventIcon,
  Visibility as ViewIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import { ListItemSecondaryAction } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import proBookingService from '../api/services/proBookingService';
import programService from '../api/services/programService';
import { useIsMobile } from '../hooks/useIsMobile';
import { ResponsiveTable, ResponsiveTableColumn } from '../components/common/ResponsiveTable';
import {
  Pro,
  ProSessionType,
  ProBooking,
  Program,
  ProgramEnrollment,
} from '../types';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => {
  return (
    <div role="tabpanel" hidden={value !== index}>
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
};

export type ProManagementScope = 'tennis' | 'swim';

interface ProManagementPageProps {
  scope?: ProManagementScope;
}

const SCOPE_CONFIG: Record<ProManagementScope, {
  title: string;
  subtitle: string;
  proDepartments: string[];
  programTypes: string[];
  defaultProgramType: string;
  emptyPros: string;
}> = {
  tennis: {
    title: 'Tennis Pros & Programs',
    subtitle: 'Manage tennis and pickleball pros, their session types, and club programs.',
    proDepartments: ['tennis_program', 'pickleball_program'],
    programTypes: ['tennis', 'pickleball'],
    defaultProgramType: 'tennis',
    emptyPros: 'No pros found. Staff members with tennis_program or pickleball_program department will appear here.',
  },
  swim: {
    title: 'Swim Pros & Programs',
    subtitle: 'Manage swim coaches, their session types, and swim programs.',
    proDepartments: ['swim_program'],
    programTypes: ['swim'],
    defaultProgramType: 'swim',
    emptyPros: 'No coaches found. Staff members with swim_program department will appear here.',
  },
};

const ProManagementPage: React.FC<ProManagementPageProps> = ({ scope = 'tennis' }) => {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const config = SCOPE_CONFIG[scope];

  // Tab state
  const [tabValue, setTabValue] = useState(0);

  // Loading states
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Data
  const [pros, setPros] = useState<Pro[]>([]);
  const [programs, setPrograms] = useState<Program[]>([]);
  const [selectedPro, setSelectedPro] = useState<Pro | null>(null);
  const [selectedProgram, setSelectedProgram] = useState<Program | null>(null);
  const [programEnrollments, setProgramEnrollments] = useState<ProgramEnrollment[]>([]);

  // Expanded rows
  const [expandedProIds, setExpandedProIds] = useState<Set<string>>(new Set());

  // Dialogs
  const [proDetailDialogOpen, setProDetailDialogOpen] = useState(false);
  const [programDetailDialogOpen, setProgramDetailDialogOpen] = useState(false);
  const [enrollmentsDialogOpen, setEnrollmentsDialogOpen] = useState(false);

  // Admin Session Type Management
  const [sessionTypeDialogOpen, setSessionTypeDialogOpen] = useState(false);
  const [editingSessionType, setEditingSessionType] = useState<ProSessionType | null>(null);
  const [sessionTypeProId, setSessionTypeProId] = useState<string>('');
  const [sessionTypeForm, setSessionTypeForm] = useState({
    name: '',
    description: '',
    duration_minutes: 60,
    max_participants: 1,
    price_member: 0,
    price_non_member: 0,
  });

  // Admin Availability Management — seven-day weekly grid
  interface DayConfig {
    enabled: boolean;
    start_time: string;
    end_time: string;
  }
  const [availabilityDialogOpen, setAvailabilityDialogOpen] = useState(false);
  const [availabilityProId, setAvailabilityProId] = useState<string>('');
  const [weeklyAvailability, setWeeklyAvailability] = useState<DayConfig[]>(() =>
    Array.from({ length: 7 }, () => ({
      enabled: false,
      start_time: '09:00',
      end_time: '17:00',
    })),
  );

  // Admin Program Management
  const [programDialogOpen, setProgramDialogOpen] = useState(false);
  const [editingProgram, setEditingProgram] = useState<Program | null>(null);
  const [programForm, setProgramForm] = useState({
    name: '',
    description: '',
    program_type: config.defaultProgramType,
    price_member: 0,
    price_non_member: 0,
    max_participants: 10,
    min_participants: 4,
    start_date: '',
    end_date: '',
  });

  // Success message
  const [success, setSuccess] = useState<string | null>(null);

  // Filters
  const [programFilter, setProgramFilter] = useState<string>('all');

  // Load data
  useEffect(() => {
    loadPros();
    loadPrograms();
  }, []);

  const loadPros = async () => {
    try {
      setLoading(true);
      const prosData = await proBookingService.getAllPros();
      setPros(prosData);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load pros');
    } finally {
      setLoading(false);
    }
  };

  const loadPrograms = async () => {
    try {
      const programsData = await programService.getAllPrograms(true);
      setPrograms(programsData);
    } catch (err: any) {
      console.error('Failed to load programs:', err);
    }
  };

  const handleViewPro = async (pro: Pro) => {
    try {
      const fullPro = await proBookingService.getProById(pro.id);
      setSelectedPro(fullPro);
      setProDetailDialogOpen(true);
    } catch (err: any) {
      setError('Failed to load pro details');
    }
  };

  const handleViewProgram = async (program: Program) => {
    try {
      const fullProgram = await programService.getProgramById(program.id);
      setSelectedProgram(fullProgram);
      setProgramDetailDialogOpen(true);
    } catch (err: any) {
      setError('Failed to load program details');
    }
  };

  const handleViewEnrollments = async (program: Program) => {
    try {
      const enrollments = await programService.getProgramEnrollments(program.id);
      setProgramEnrollments(enrollments);
      setSelectedProgram(program);
      setEnrollmentsDialogOpen(true);
    } catch (err: any) {
      setError('Failed to load enrollments');
    }
  };

  const toggleProExpanded = (proId: string) => {
    const newExpanded = new Set(expandedProIds);
    if (newExpanded.has(proId)) {
      newExpanded.delete(proId);
    } else {
      newExpanded.add(proId);
    }
    setExpandedProIds(newExpanded);
  };

  // Scope-aware filtering: only show pros + programs that belong to the current
  // scope (tennis vs swim). Inside that scope, the status filter still applies.
  // Backend stores departments in uppercase ('SWIM_PROGRAM') while the scope
  // config uses lowercase enum values ('swim_program'); lowercase both sides
  // so the comparison is case-insensitive.
  const scopedPros = pros.filter((p) =>
    (p.departments || []).some((d) =>
      config.proDepartments.includes(String(d).toLowerCase()),
    ),
  );
  const scopedPrograms = programs.filter((p) =>
    config.programTypes.includes(String(p.program_type).toLowerCase()),
  );

  const getFilteredPrograms = () => {
    if (programFilter === 'all') return scopedPrograms;
    return scopedPrograms.filter(p => p.status === programFilter);
  };

  const getDepartmentChips = (departments: string[]) => {
    const deptLabels: Record<string, { label: string; color: 'primary' | 'secondary' | 'success' | 'warning' }> = {
      tennis_program: { label: 'Tennis', color: 'primary' },
      pickleball_program: { label: 'Pickleball', color: 'secondary' },
      swim_program: { label: 'Swim', color: 'success' },
    };

    return departments
      .map((d) => ({ raw: d, key: String(d).toLowerCase() }))
      .filter(({ key }) => deptLabels[key])
      .map(({ raw, key }) => (
        <Chip
          key={raw}
          label={deptLabels[key].label}
          color={deptLabels[key].color}
          size="small"
          sx={{ mr: 0.5 }}
        />
      ));
  };

  const dayLabels = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

  // ==================== Session Type Handlers ====================

  const handleAddSessionType = (proId: string) => {
    setSessionTypeProId(proId);
    setEditingSessionType(null);
    setSessionTypeForm({
      name: '',
      description: '',
      duration_minutes: 60,
      max_participants: 1,
      price_member: 0,
      price_non_member: 0,
    });
    setSessionTypeDialogOpen(true);
  };

  const handleEditSessionType = (proId: string, sessionType: ProSessionType) => {
    setSessionTypeProId(proId);
    setEditingSessionType(sessionType);
    setSessionTypeForm({
      name: sessionType.name,
      description: sessionType.description || '',
      duration_minutes: sessionType.duration_minutes,
      max_participants: sessionType.max_participants,
      price_member: sessionType.price_member,
      price_non_member: sessionType.price_non_member || 0,
    });
    setSessionTypeDialogOpen(true);
  };

  const handleSaveSessionType = async () => {
    try {
      if (editingSessionType) {
        await proBookingService.updateSessionType(editingSessionType.id, sessionTypeForm);
        setSuccess('Session type updated successfully!');
      } else {
        await proBookingService.adminCreateSessionType(sessionTypeProId, sessionTypeForm);
        setSuccess('Session type created successfully!');
      }
      setSessionTypeDialogOpen(false);
      loadPros();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to save session type');
    }
  };

  const handleDeleteSessionType = async (sessionTypeId: string) => {
    if (!window.confirm('Are you sure you want to delete this session type?')) return;
    try {
      await proBookingService.deleteSessionType(sessionTypeId);
      setSuccess('Session type deleted successfully!');
      loadPros();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to delete session type');
    }
  };

  // ==================== Availability Handlers ====================

  const handleEditAvailability = (proId: string, availability: any[]) => {
    setAvailabilityProId(proId);
    // Project incoming slots onto a fixed 7-day grid. If a coach has split
    // shifts on a single day in the data, this collapses to the first slot
    // for that day — the new UI is one row per day, prioritizing the common
    // case (one block of hours per weekday).
    const next: DayConfig[] = Array.from({ length: 7 }, () => ({
      enabled: false,
      start_time: '09:00',
      end_time: '17:00',
    }));
    for (const slot of availability) {
      const d = Number(slot.day_of_week);
      if (d < 0 || d > 6) continue;
      if (!next[d].enabled) {
        next[d] = {
          enabled: true,
          start_time: slot.start_time || '09:00',
          end_time: slot.end_time || '17:00',
        };
      }
    }
    setWeeklyAvailability(next);
    setAvailabilityDialogOpen(true);
  };

  const updateWeeklyDay = (day: number, patch: Partial<DayConfig>) => {
    setWeeklyAvailability((prev) =>
      prev.map((d, i) => (i === day ? { ...d, ...patch } : d)),
    );
  };

  const handleSaveAvailability = async () => {
    try {
      const slots = weeklyAvailability
        .map((cfg, day) =>
          cfg.enabled
            ? {
                day_of_week: day,
                start_time: cfg.start_time,
                end_time: cfg.end_time,
                is_active: true,
              }
            : null,
        )
        .filter((s): s is NonNullable<typeof s> => s !== null);
      await proBookingService.adminUpdateAvailability(availabilityProId, slots);
      setSuccess('Availability updated successfully!');
      setAvailabilityDialogOpen(false);
      loadPros();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to update availability');
    }
  };

  // ==================== Program Handlers ====================

  const handleEditProgram = (program: Program) => {
    setEditingProgram(program);
    setProgramForm({
      name: program.name,
      description: program.description || '',
      program_type: program.program_type,
      price_member: program.price_member,
      price_non_member: program.price_non_member || 0,
      max_participants: program.max_participants,
      min_participants: program.min_participants || 4,
      start_date: program.start_date,
      end_date: program.end_date,
    });
    setProgramDialogOpen(true);
  };

  const handleSaveProgram = async () => {
    try {
      if (editingProgram) {
        await programService.updateProgram(editingProgram.id, programForm as any);
        setSuccess('Program updated successfully!');
      }
      setProgramDialogOpen(false);
      loadPrograms();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to save program');
    }
  };

  const handleDeleteProgram = async (programId: string) => {
    if (!window.confirm('Are you sure you want to cancel this program?')) return;
    try {
      await programService.cancelProgram(programId);
      setSuccess('Program cancelled successfully!');
      loadPrograms();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to cancel program');
    }
  };

  // Render Pros Tab
  const renderProsTab = () => (
    <>
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: 'center' }, gap: { xs: 1, sm: 0 }, mb: 3 }}>
        <Typography variant="h6">
          <PersonIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
          {scope === 'swim' ? 'Swim Coaches' : 'Tennis & Pickleball Pros'} ({scopedPros.length})
        </Typography>
        <Button variant="outlined" startIcon={<RefreshIcon />} onClick={loadPros}>
          Refresh
        </Button>
      </Box>

      {loading ? (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress />
        </Box>
      ) : scopedPros.length === 0 ? (
        <Alert severity="info">{config.emptyPros}</Alert>
      ) : (
        <>
          {/* Desktop: original table with expandable rows */}
          <Box sx={{ display: { xs: 'none', md: 'block' } }}>
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell width={50}></TableCell>
                    <TableCell>Name</TableCell>
                    <TableCell>Departments</TableCell>
                    <TableCell>Session Types</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {scopedPros.map((pro) => (
                    <React.Fragment key={pro.id}>
                      <TableRow hover>
                        <TableCell>
                          <IconButton size="small" onClick={() => toggleProExpanded(pro.id)}>
                            {expandedProIds.has(pro.id) ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                          </IconButton>
                        </TableCell>
                        <TableCell>
                          <Typography variant="subtitle2">
                            {pro.first_name} {pro.last_name}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {getDepartmentChips(pro.departments || [])}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={`${pro.session_types?.length || 0} types`}
                            size="small"
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>
                          <Tooltip title="View Details">
                            <IconButton size="small" onClick={() => handleViewPro(pro)}>
                              <ViewIcon />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell colSpan={5} sx={{ py: 0, borderBottom: expandedProIds.has(pro.id) ? undefined : 'none' }}>
                          <Collapse in={expandedProIds.has(pro.id)} timeout="auto" unmountOnExit>
                            <Box sx={{ py: 2, px: 4, bgcolor: 'grey.50' }}>
                              <Grid container spacing={3}>
                                <Grid size={{ xs: 12, md: 6 }}>
                                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                    <Typography variant="subtitle2">
                                      <AccessTimeIcon sx={{ mr: 1, verticalAlign: 'middle', fontSize: 18 }} />
                                      Session Types
                                    </Typography>
                                    <Tooltip title="Add Session Type">
                                      <IconButton size="small" onClick={() => handleAddSessionType(pro.id)}>
                                        <AddIcon fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                  </Box>
                                  {pro.session_types && pro.session_types.length > 0 ? (
                                    <List dense>
                                      {pro.session_types.map((st) => (
                                        <ListItem key={st.id} sx={{ px: 0 }}>
                                          <ListItemText
                                            primary={st.name}
                                            secondary={`${st.duration_minutes} min | ${proBookingService.formatPrice(st.price_member)} (member)`}
                                          />
                                          <ListItemSecondaryAction>
                                            <IconButton size="small" onClick={() => handleEditSessionType(pro.id, st)}>
                                              <EditIcon fontSize="small" />
                                            </IconButton>
                                            <IconButton size="small" color="error" onClick={() => handleDeleteSessionType(st.id)}>
                                              <DeleteIcon fontSize="small" />
                                            </IconButton>
                                          </ListItemSecondaryAction>
                                        </ListItem>
                                      ))}
                                    </List>
                                  ) : (
                                    <Typography variant="body2" color="text.secondary">
                                      No session types configured
                                    </Typography>
                                  )}
                                </Grid>
                                <Grid size={{ xs: 12, md: 6 }}>
                                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                    <Typography variant="subtitle2">
                                      <EventIcon sx={{ mr: 1, verticalAlign: 'middle', fontSize: 18 }} />
                                      Availability
                                    </Typography>
                                    <Tooltip title="Edit Availability">
                                      <IconButton size="small" onClick={() => handleEditAvailability(pro.id, pro.availability || [])}>
                                        <EditIcon fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                  </Box>
                                  {pro.availability && pro.availability.length > 0 ? (
                                    <List dense>
                                      {pro.availability.map((av, idx) => (
                                        <ListItem key={idx} sx={{ px: 0 }}>
                                          <ListItemText
                                            primary={proBookingService.getDayName(av.day_of_week)}
                                            secondary={`${av.start_time} - ${av.end_time}`}
                                          />
                                        </ListItem>
                                      ))}
                                    </List>
                                  ) : (
                                    <Typography variant="body2" color="text.secondary">
                                      No availability set
                                    </Typography>
                                  )}
                                </Grid>
                              </Grid>
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>

          {/* Mobile: card stack — session types + availability always expanded (vertical room is free on phones) */}
          <Stack spacing={1.5} sx={{ display: { xs: 'flex', md: 'none' } }}>
            {scopedPros.map((pro) => (
              <Paper key={pro.id} variant="outlined" sx={{ p: 2 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1} sx={{ mb: 1 }}>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="subtitle1"><strong>{pro.first_name} {pro.last_name}</strong></Typography>
                    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
                      {getDepartmentChips(pro.departments || [])}
                    </Stack>
                  </Box>
                  <Tooltip title="View Details">
                    <IconButton onClick={() => handleViewPro(pro)}>
                      <ViewIcon />
                    </IconButton>
                  </Tooltip>
                </Stack>

                <Divider sx={{ my: 1 }} />

                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
                  <Typography variant="subtitle2">
                    <AccessTimeIcon sx={{ mr: 0.5, verticalAlign: 'middle', fontSize: 18 }} />
                    Session Types
                  </Typography>
                  <Tooltip title="Add Session Type">
                    <IconButton onClick={() => handleAddSessionType(pro.id)}>
                      <AddIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Stack>
                {pro.session_types && pro.session_types.length > 0 ? (
                  <List dense disablePadding>
                    {pro.session_types.map((st) => (
                      <ListItem key={st.id} sx={{ px: 0 }}>
                        <ListItemText
                          primary={st.name}
                          secondary={`${st.duration_minutes} min · ${proBookingService.formatPrice(st.price_member)} (member)`}
                        />
                        <ListItemSecondaryAction>
                          <IconButton onClick={() => handleEditSessionType(pro.id, st)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton color="error" onClick={() => handleDeleteSessionType(st.id)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </ListItemSecondaryAction>
                      </ListItem>
                    ))}
                  </List>
                ) : (
                  <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                    No session types configured
                  </Typography>
                )}

                <Divider sx={{ my: 1 }} />

                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
                  <Typography variant="subtitle2">
                    <EventIcon sx={{ mr: 0.5, verticalAlign: 'middle', fontSize: 18 }} />
                    Availability
                  </Typography>
                  <Tooltip title="Edit Availability">
                    <IconButton onClick={() => handleEditAvailability(pro.id, pro.availability || [])}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Stack>
                {pro.availability && pro.availability.length > 0 ? (
                  <List dense disablePadding>
                    {pro.availability.map((av, idx) => (
                      <ListItem key={idx} sx={{ px: 0 }}>
                        <ListItemText
                          primary={proBookingService.getDayName(av.day_of_week)}
                          secondary={`${av.start_time} – ${av.end_time}`}
                        />
                      </ListItem>
                    ))}
                  </List>
                ) : (
                  <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                    No availability set
                  </Typography>
                )}
              </Paper>
            ))}
          </Stack>
        </>
      )}
    </>
  );

  // Render Programs Tab
  const renderProgramsTab = () => (
    <>
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: 'center' }, gap: { xs: 1, sm: 0 }, mb: 3 }}>
        <Typography variant="h6">
          <SchoolIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
          All Programs ({getFilteredPrograms().length})
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={programFilter}
              label="Status"
              onChange={(e) => setProgramFilter(e.target.value)}
            >
              <MenuItem value="all">All Statuses</MenuItem>
              <MenuItem value="draft">Draft</MenuItem>
              <MenuItem value="published">Published</MenuItem>
              <MenuItem value="cancelled">Cancelled</MenuItem>
              <MenuItem value="completed">Completed</MenuItem>
            </Select>
          </FormControl>
          <Button variant="outlined" startIcon={<RefreshIcon />} onClick={loadPrograms}>
            Refresh
          </Button>
        </Box>
      </Box>

      {(() => {
        const renderEnrollmentChip = (program: Program) => (
          <Chip
            label={`${program.enrollment_count || 0}/${program.max_participants}`}
            size="small"
            variant="outlined"
            onClick={() => handleViewEnrollments(program)}
            sx={{ cursor: 'pointer' }}
            icon={<GroupIcon />}
          />
        );
        const renderActions = (program: Program) => (
          <>
            <Tooltip title="View Details">
              <IconButton onClick={() => handleViewProgram(program)}>
                <ViewIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Edit Program">
              <IconButton onClick={() => handleEditProgram(program)}>
                <EditIcon />
              </IconButton>
            </Tooltip>
            {program.status !== 'cancelled' && (
              <Tooltip title="Cancel Program">
                <IconButton color="error" onClick={() => handleDeleteProgram(program.id)}>
                  <DeleteIcon />
                </IconButton>
              </Tooltip>
            )}
          </>
        );
        const renderStatusChip = (program: Program) => (
          <Chip
            label={programService.getProgramStatusLabel(program.status).label}
            color={programService.getProgramStatusLabel(program.status).color as any}
            size="small"
          />
        );
        const filtered = getFilteredPrograms();

        const columns: ResponsiveTableColumn<Program>[] = [
          {
            key: 'name',
            label: 'Program Name',
            renderCell: (p) => <Typography variant="subtitle2">{p.name}</Typography>,
          },
          {
            key: 'type',
            label: 'Type',
            renderCell: (p) => (
              <Chip
                label={programService.getProgramTypeLabel(p.program_type)}
                size="small"
                variant="outlined"
              />
            ),
          },
          {
            key: 'instructor',
            label: 'Instructor',
            renderCell: (p) => p.instructor
              ? <>{`${p.instructor.first_name} ${p.instructor.last_name}`}</>
              : <Typography variant="body2" color="text.secondary">Not assigned</Typography>,
          },
          {
            key: 'dates',
            label: 'Dates',
            renderCell: (p) => (
              <Typography variant="body2">
                {programService.formatDateRange(p.start_date, p.end_date)}
              </Typography>
            ),
          },
          { key: 'status', label: 'Status', renderCell: renderStatusChip },
          { key: 'enrollment', label: 'Enrollment', renderCell: renderEnrollmentChip },
          {
            key: 'price',
            label: 'Price',
            renderCell: (p) => (
              <Typography variant="body2">{proBookingService.formatPrice(p.price_member)}</Typography>
            ),
          },
          { key: 'actions', label: 'Actions', renderCell: renderActions },
        ];

        return (
          <ResponsiveTable<Program>
            rows={filtered}
            columns={columns}
            rowKey={(p) => p.id}
            emptyState={
              <Alert severity="info">
                No programs found. Staff can create programs from the Court Booking page.
              </Alert>
            }
            renderMobileCard={(p) => (
              <Stack spacing={1}>
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="subtitle1"><strong>{p.name}</strong></Typography>
                    <Typography variant="caption" color="text.secondary">
                      {p.instructor
                        ? `${p.instructor.first_name} ${p.instructor.last_name}`
                        : 'No instructor'}
                    </Typography>
                  </Box>
                  {renderStatusChip(p)}
                </Stack>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  <Chip
                    label={programService.getProgramTypeLabel(p.program_type)}
                    size="small"
                    variant="outlined"
                  />
                  {renderEnrollmentChip(p)}
                  <Chip
                    label={proBookingService.formatPrice(p.price_member)}
                    size="small"
                    variant="outlined"
                  />
                </Stack>
                <Typography variant="caption" color="text.secondary">
                  {programService.formatDateRange(p.start_date, p.end_date)}
                </Typography>
                <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                  {renderActions(p)}
                </Stack>
              </Stack>
            )}
          />
        );
      })()}
    </>
  );

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Typography variant="h4" gutterBottom>
        {config.title}
      </Typography>
      <Typography variant="body1" color="text.secondary" paragraph>
        {config.subtitle}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Tabs
        value={tabValue}
        onChange={(_, v) => setTabValue(v)}
        variant="scrollable"
        scrollButtons="auto"
        allowScrollButtonsMobile
        sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}
      >
        <Tab label="Pros" icon={<PersonIcon />} iconPosition="start" />
        <Tab label="Programs" icon={<SchoolIcon />} iconPosition="start" />
      </Tabs>

      <TabPanel value={tabValue} index={0}>
        {renderProsTab()}
      </TabPanel>

      <TabPanel value={tabValue} index={1}>
        {renderProgramsTab()}
      </TabPanel>

      {/* Pro Detail Dialog */}
      <Dialog open={proDetailDialogOpen} onClose={() => setProDetailDialogOpen(false)} maxWidth="md" fullWidth fullScreen={isMobile}>
        <DialogTitle>
          <PersonIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
          {selectedPro?.first_name} {selectedPro?.last_name}
        </DialogTitle>
        <DialogContent>
          {selectedPro && (
            <Grid container spacing={3} sx={{ mt: 1 }}>
              <Grid size={{ xs: 12, md: 6 }}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      <AccessTimeIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                      Session Types
                    </Typography>
                    {selectedPro.session_types && selectedPro.session_types.length > 0 ? (
                      <List>
                        {selectedPro.session_types.map((st) => (
                          <ListItem key={st.id} divider>
                            <ListItemText
                              primary={st.name}
                              secondary={
                                <>
                                  Duration: {st.duration_minutes} min | Max {st.max_participants} participant(s)
                                  <br />
                                  Member: {proBookingService.formatPrice(st.price_member)}
                                  {st.price_non_member && (
                                    <> | Non-Member: {proBookingService.formatPrice(st.price_non_member)}</>
                                  )}
                                </>
                              }
                            />
                          </ListItem>
                        ))}
                      </List>
                    ) : (
                      <Alert severity="info">No session types configured</Alert>
                    )}
                  </CardContent>
                </Card>
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      <EventIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                      Weekly Availability
                    </Typography>
                    {selectedPro.availability && selectedPro.availability.length > 0 ? (
                      <List>
                        {selectedPro.availability.map((av, idx) => (
                          <ListItem key={idx} divider>
                            <ListItemText
                              primary={proBookingService.getDayName(av.day_of_week)}
                              secondary={`${av.start_time} - ${av.end_time}`}
                            />
                            <Chip
                              label={av.is_active ? 'Active' : 'Inactive'}
                              color={av.is_active ? 'success' : 'default'}
                              size="small"
                            />
                          </ListItem>
                        ))}
                      </List>
                    ) : (
                      <Alert severity="info">No availability schedule set</Alert>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setProDetailDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Program Detail Dialog */}
      <Dialog open={programDetailDialogOpen} onClose={() => setProgramDetailDialogOpen(false)} maxWidth="md" fullWidth fullScreen={isMobile}>
        <DialogTitle>
          <SchoolIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
          {selectedProgram?.name}
        </DialogTitle>
        <DialogContent>
          {selectedProgram && (
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid size={{ xs: 12 }}>
                <Typography variant="body1" paragraph>
                  {selectedProgram.description || 'No description provided'}
                </Typography>
              </Grid>
              <Grid size={{ xs: 6 }}>
                <Typography variant="subtitle2" color="text.secondary">Type</Typography>
                <Chip label={programService.getProgramTypeLabel(selectedProgram.program_type)} size="small" />
              </Grid>
              <Grid size={{ xs: 6 }}>
                <Typography variant="subtitle2" color="text.secondary">Status</Typography>
                <Chip
                  label={programService.getProgramStatusLabel(selectedProgram.status).label}
                  color={programService.getProgramStatusLabel(selectedProgram.status).color as any}
                  size="small"
                />
              </Grid>
              <Grid size={{ xs: 6 }}>
                <Typography variant="subtitle2" color="text.secondary">Instructor</Typography>
                <Typography>
                  {selectedProgram.instructor
                    ? `${selectedProgram.instructor.first_name} ${selectedProgram.instructor.last_name}`
                    : 'Not assigned'}
                </Typography>
              </Grid>
              <Grid size={{ xs: 6 }}>
                <Typography variant="subtitle2" color="text.secondary">Dates</Typography>
                <Typography>
                  {programService.formatDateRange(selectedProgram.start_date, selectedProgram.end_date)}
                </Typography>
              </Grid>
              <Grid size={{ xs: 6 }}>
                <Typography variant="subtitle2" color="text.secondary">Member Price</Typography>
                <Typography>{proBookingService.formatPrice(selectedProgram.price_member)}</Typography>
              </Grid>
              <Grid size={{ xs: 6 }}>
                <Typography variant="subtitle2" color="text.secondary">Non-Member Price</Typography>
                <Typography>
                  {selectedProgram.price_non_member
                    ? proBookingService.formatPrice(selectedProgram.price_non_member)
                    : 'Not available'}
                </Typography>
              </Grid>
              <Grid size={{ xs: 6 }}>
                <Typography variant="subtitle2" color="text.secondary">Capacity</Typography>
                <Typography>
                  {selectedProgram.enrollment_count || 0} / {selectedProgram.max_participants} enrolled
                </Typography>
              </Grid>
              {selectedProgram.recurrence_pattern && (
                <Grid size={{ xs: 6 }}>
                  <Typography variant="subtitle2" color="text.secondary">Schedule</Typography>
                  <Typography>
                    {programService.formatRecurrencePattern(selectedProgram.recurrence_pattern)}
                  </Typography>
                </Grid>
              )}
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => handleViewEnrollments(selectedProgram!)} color="primary">
            View Enrollments
          </Button>
          <Button onClick={() => setProgramDetailDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Enrollments Dialog */}
      <Dialog open={enrollmentsDialogOpen} onClose={() => setEnrollmentsDialogOpen(false)} maxWidth="md" fullWidth fullScreen={isMobile}>
        <DialogTitle>
          <GroupIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
          Enrollments - {selectedProgram?.name}
        </DialogTitle>
        <DialogContent>
          {programEnrollments.length === 0 ? (
            <Alert severity="info" sx={{ mt: 2 }}>No enrollments yet</Alert>
          ) : (
            <TableContainer sx={{ mt: 2 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Payment</TableCell>
                    <TableCell>Enrolled</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {programEnrollments.map((enrollment) => {
                    // The API returns the resolved swimmer under `enrollee` (member,
                    // non-member, or either's dependent). is_member is the policy flag —
                    // a non-member adult enrolls via member_id but is still a non-member.
                    const enrollee = enrollment.enrollee;
                    const enrolleeName = enrollee
                      ? `${enrollee.first_name ?? ''} ${enrollee.last_name ?? ''}`.trim()
                      : '';
                    const isMember = enrollee?.is_member
                      ?? (enrollee?.type === 'member' || enrollee?.type === 'dependent');
                    return (
                    <TableRow key={enrollment.id}>
                      <TableCell>
                        {enrolleeName || '—'}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={isMember ? 'Member' : 'Non-Member'}
                          size="small"
                          color={isMember ? 'primary' : 'secondary'}
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={programService.getEnrollmentStatusLabel(enrollment.status).label}
                          color={programService.getEnrollmentStatusLabel(enrollment.status).color as any}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={enrollment.payment_status}
                          size="small"
                          color={enrollment.payment_status === 'paid' ? 'success' : 'warning'}
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        {new Date(enrollment.enrolled_at).toLocaleDateString()}
                      </TableCell>
                    </TableRow>
                  );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEnrollmentsDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Session Type Dialog */}
      <Dialog open={sessionTypeDialogOpen} onClose={() => setSessionTypeDialogOpen(false)} maxWidth="sm" fullWidth fullScreen={isMobile}>
        <DialogTitle>{editingSessionType ? 'Edit Session Type' : 'Add Session Type'}</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <TextField
              fullWidth
              label="Session Name"
              value={sessionTypeForm.name}
              onChange={(e) => setSessionTypeForm({ ...sessionTypeForm, name: e.target.value })}
              placeholder="e.g., Private Lesson, Group Clinic"
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="Description"
              value={sessionTypeForm.description}
              onChange={(e) => setSessionTypeForm({ ...sessionTypeForm, description: e.target.value })}
              placeholder="Describe what this session includes"
              multiline
              rows={2}
              sx={{ mb: 2 }}
            />
            <Grid container spacing={2}>
              <Grid size={{ xs: 6 }}>
                <FormControl fullWidth>
                  <InputLabel>Duration</InputLabel>
                  <Select
                    value={sessionTypeForm.duration_minutes}
                    label="Duration"
                    onChange={(e) => setSessionTypeForm({ ...sessionTypeForm, duration_minutes: Number(e.target.value) })}
                  >
                    <MenuItem value={30}>30 minutes</MenuItem>
                    <MenuItem value={60}>1 hour</MenuItem>
                    <MenuItem value={90}>1.5 hours</MenuItem>
                    <MenuItem value={120}>2 hours</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid size={{ xs: 6 }}>
                <TextField
                  fullWidth
                  label="Max Participants"
                  type="number"
                  value={sessionTypeForm.max_participants}
                  onChange={(e) => setSessionTypeForm({ ...sessionTypeForm, max_participants: Number(e.target.value) })}
                  inputProps={{ min: 1, max: 20 }}
                />
              </Grid>
              <Grid size={{ xs: 6 }}>
                <TextField
                  fullWidth
                  label="Member Price ($)"
                  type="number"
                  value={sessionTypeForm.price_member}
                  onChange={(e) => setSessionTypeForm({ ...sessionTypeForm, price_member: Number(e.target.value) })}
                  inputProps={{ min: 0, step: 5 }}
                />
              </Grid>
              <Grid size={{ xs: 6 }}>
                <TextField
                  fullWidth
                  label="Non-Member Price ($)"
                  type="number"
                  value={sessionTypeForm.price_non_member}
                  onChange={(e) => setSessionTypeForm({ ...sessionTypeForm, price_non_member: Number(e.target.value) })}
                  inputProps={{ min: 0, step: 5 }}
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSessionTypeDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSaveSessionType}
            disabled={!sessionTypeForm.name || sessionTypeForm.price_member <= 0}
          >
            {editingSessionType ? 'Save Changes' : 'Create Session Type'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Availability Dialog */}
      <Dialog
        open={availabilityDialogOpen}
        onClose={() => setAvailabilityDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>Edit Pro Availability</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Check the days this pro is available. Members will be able to book
            lessons during the times you set.
          </Typography>

          {/* Quick bulk helpers */}
          <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap' }}>
            <Button
              size="small"
              variant="outlined"
              onClick={() => {
                const ref = weeklyAvailability[0]; // Mon as template
                setWeeklyAvailability((prev) =>
                  prev.map((d, i) => (i <= 4 ? { enabled: true, start_time: ref.start_time, end_time: ref.end_time } : d)),
                );
              }}
            >
              Weekdays from Mon
            </Button>
            <Button
              size="small"
              variant="outlined"
              onClick={() =>
                setWeeklyAvailability((prev) => prev.map(() => ({ enabled: false, start_time: '09:00', end_time: '17:00' })))
              }
            >
              Clear all
            </Button>
          </Stack>

          <Stack spacing={1}>
            {dayLabels.map((label, day) => {
              const cfg = weeklyAvailability[day];
              return (
                <Box
                  key={day}
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: 'auto 1fr 1fr',
                    alignItems: 'center',
                    gap: 1.5,
                    p: 1,
                    borderRadius: 1,
                    bgcolor: cfg.enabled ? 'transparent' : 'rgba(0,0,0,0.02)',
                  }}
                >
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={cfg.enabled}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                          updateWeeklyDay(day, { enabled: e.target.checked })
                        }
                      />
                    }
                    label={label}
                    sx={{ minWidth: 110, m: 0 }}
                  />
                  <TextField
                    size="small"
                    label="Start"
                    type="time"
                    value={cfg.start_time}
                    disabled={!cfg.enabled}
                    onChange={(e) => updateWeeklyDay(day, { start_time: e.target.value })}
                    slotProps={{ inputLabel: { shrink: true } }}
                  />
                  <TextField
                    size="small"
                    label="End"
                    type="time"
                    value={cfg.end_time}
                    disabled={!cfg.enabled}
                    onChange={(e) => updateWeeklyDay(day, { end_time: e.target.value })}
                    slotProps={{ inputLabel: { shrink: true } }}
                  />
                </Box>
              );
            })}
          </Stack>
        </DialogContent>
        <DialogActions
          sx={{
            flexDirection: { xs: 'column-reverse', sm: 'row' },
            gap: { xs: 1, sm: 0 },
            '& > button': { width: { xs: '100%', sm: 'auto' }, ml: { xs: '0 !important', sm: undefined } },
          }}
        >
          <Button onClick={() => setAvailabilityDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveAvailability}>
            Save Availability
          </Button>
        </DialogActions>
      </Dialog>

      {/* Program Edit Dialog */}
      <Dialog open={programDialogOpen} onClose={() => setProgramDialogOpen(false)} maxWidth="sm" fullWidth fullScreen={isMobile}>
        <DialogTitle>Edit Program</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <TextField
              fullWidth
              label="Program Name"
              value={programForm.name}
              onChange={(e) => setProgramForm({ ...programForm, name: e.target.value })}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="Description"
              value={programForm.description}
              onChange={(e) => setProgramForm({ ...programForm, description: e.target.value })}
              multiline
              rows={2}
              sx={{ mb: 2 }}
            />
            <Grid container spacing={2}>
              <Grid size={{ xs: 6 }}>
                <FormControl fullWidth>
                  <InputLabel>Type</InputLabel>
                  <Select
                    value={programForm.program_type}
                    label="Type"
                    onChange={(e) => setProgramForm({ ...programForm, program_type: e.target.value })}
                  >
                    <MenuItem value="tennis">Tennis</MenuItem>
                    <MenuItem value="pickleball">Pickleball</MenuItem>
                    <MenuItem value="fitness">Fitness</MenuItem>
                    <MenuItem value="swim">Swim</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid size={{ xs: 6 }}>
                <TextField
                  fullWidth
                  label="Max Participants"
                  type="number"
                  value={programForm.max_participants}
                  onChange={(e) => setProgramForm({ ...programForm, max_participants: Number(e.target.value) })}
                  inputProps={{ min: 1 }}
                />
              </Grid>
              <Grid size={{ xs: 6 }}>
                <TextField
                  fullWidth
                  label="Member Price ($)"
                  type="number"
                  value={programForm.price_member}
                  onChange={(e) => setProgramForm({ ...programForm, price_member: Number(e.target.value) })}
                  inputProps={{ min: 0, step: 5 }}
                />
              </Grid>
              <Grid size={{ xs: 6 }}>
                <TextField
                  fullWidth
                  label="Non-Member Price ($)"
                  type="number"
                  value={programForm.price_non_member}
                  onChange={(e) => setProgramForm({ ...programForm, price_non_member: Number(e.target.value) })}
                  inputProps={{ min: 0, step: 5 }}
                />
              </Grid>
              <Grid size={{ xs: 6 }}>
                <TextField
                  fullWidth
                  label="Start Date"
                  type="date"
                  value={programForm.start_date}
                  onChange={(e) => setProgramForm({ ...programForm, start_date: e.target.value })}
                  slotProps={{ inputLabel: { shrink: true } }}
                />
              </Grid>
              <Grid size={{ xs: 6 }}>
                <TextField
                  fullWidth
                  label="End Date"
                  type="date"
                  value={programForm.end_date}
                  onChange={(e) => setProgramForm({ ...programForm, end_date: e.target.value })}
                  slotProps={{ inputLabel: { shrink: true } }}
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setProgramDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSaveProgram}
            disabled={!programForm.name || !programForm.start_date || !programForm.end_date}
          >
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success/Error Snackbar */}
      {success && (
        <Alert
          severity="success"
          sx={{ position: 'fixed', bottom: 16, right: 16, zIndex: 9999 }}
          onClose={() => setSuccess(null)}
        >
          {success}
        </Alert>
      )}
    </Container>
  );
};

export default ProManagementPage;
